import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { UsersService } from '../components/administration/users/users.service';

// Runs before any routed component under the authenticated layout is created, so every
// component that reads AuthService.mappedDevices already has the real per-user value
// (instead of racing each other to fetch and seed it themselves on ngOnInit).
//
// The user id is taken from the server (_me), never from localStorage — localStorage.user_id
// is client-editable, so trusting it here would let someone see another user's mapped devices
// just by changing that value.
@Injectable({ providedIn: 'root' })
export class MappedDevicesGuard implements CanActivate {
  // MappedDevicesGuard is a root singleton that outlives any single login session, so this
  // flag alone must never be the only thing deciding whether to fetch: without reset() being
  // called at logout, a second user logging in in the same tab (no full page reload) would
  // silently keep seeing the first user's mapped devices, since canActivate() would short-circuit.
  private loaded = false;

  constructor(private authService: AuthService, private usersService: UsersService) {}

  canActivate(): Observable<boolean> {
    if (this.loaded) {
      return of(true);
    }
    return this.refresh();
  }

  // Always re-fetches, ignoring the cached `loaded` state canActivate() relies on. Call this
  // right after a successful login so the mapping is current for *this* user immediately,
  // rather than trusting whatever canActivate() last cached.
  refresh(): Observable<boolean> {
    return this.authService.getCurrentUser().pipe(
      switchMap((res: any) => {
        const user = res?.result?._id;
        if (!user) {
          this.authService.sendMappedDevices({});
          this.loaded = true;
          return of(true);
        }
        return this.usersService.getUserDevices(user).pipe(
          map((devicesRes: any) => {
            const devices = devicesRes?.result?.hits?.[0]?._source?.devicesList?.[0]?.devices || {};
            this.authService.sendMappedDevices(devices);
            this.loaded = true;
            return true;
          })
        );
      }),
      catchError(() => {
        this.authService.sendMappedDevices({});
        this.loaded = true;
        return of(true);
      })
    );
  }

  // Call at logout so the next login (same tab, no full reload) doesn't inherit a stale
  // `loaded` state and skip re-fetching the newly-logged-in user's mapping.
  reset(): void {
    this.loaded = false;
    this.authService.sendMappedDevices({});
  }
}
